import classNames from "classnames";
import { useContext, useEffect, useState } from "react";

import Loader from "src/app/components/loader/Loader";
import DeleteModal from "src/app/components/modal/DeleteModal";
import { ModalContent } from "src/app/components/modal/types";
import Header from "src/app/modules/calculator/components/calculation/Header";
import System from "src/app/modules/calculator/components/calculation/System";
import Total from "src/app/modules/calculator/components/calculation/Total";
import { ProjectSystem, SYSTEM_PARAMETER_TYPE } from "src/app/modules/calculator/types";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { ProjectResponse } from "src/core/models/projects";
import { SystemResponse } from "src/core/models/systems";
import { ProjectsService } from "src/core/services/projects";
import { SystemsService } from "src/core/services/systems";

import calcCss from "./components/calculation/Calculation.module.css";
import Sidebar from "./components/calculation/Sidebar";

function Calculator({
  systemId,
  projectId,
  clearSystemAdded,
  showAuthModal,
  showPartnerModal,
  removeProjectId,
}: {
  systemId: number;
  projectId: string | null;
  clearSystemAdded: () => void;
  showAuthModal: (formType: string) => void;
  showPartnerModal: (isOpen: boolean) => void;
  removeProjectId: () => void;
}) {
  const [activeSystemIndex, setActiveSystemIndex] = useState<number>(0);
  const [selectedSystems, setSelectedSystems] = useState<Array<ProjectSystem>>([]);
  const [loadingSystem, setLoadingSystem] = useState(false);
  const [loadingAllSystems, setLoadingAllSystems] = useState(true);
  const [totalActive, setTotalActive] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalContent, setDeleteModalContent] = useState<ModalContent>();

  const modalData = useContext(ModalContext);

  const activeSystemElement = selectedSystems.find((x) => {
    return x.index === activeSystemIndex;
  });

  function handleSystemRename(index: number, newName: string | null | undefined) {
    if (activeSystemIndex !== index) {
      setActiveSystemIndex(index);
    }
    let renamedSystem = selectedSystems.find((x) => x.index === index);
    if (renamedSystem) {
      renamedSystem.name = newName ?? "";
    }
    let copyOfSelectedSystems = [...selectedSystems.map((x) => x)];
    setSelectedSystems(copyOfSelectedSystems);
  }
  function handleSystemDuplicate(index: number | null) {
    if (index != null) {
      if (activeSystemIndex !== index) {
        setActiveSystemIndex(index);
      }
      let duplicatedSystem = Object.assign(
        {},
        selectedSystems.find((x) => x.index === index)
      );
      let copyOfSelectedSystems = [...selectedSystems.map((x, idx) => ({ ...x, index: idx }))];
      if (duplicatedSystem) {
        duplicatedSystem.index = selectedSystems.length;
        copyOfSelectedSystems.push(duplicatedSystem);
        setSelectedSystems(copyOfSelectedSystems);
      }
    }
  }
  function handleSystemDelete(index: number) {
    let systemForDeletion = selectedSystems.find((x) => x.index === index);
    setDeleteModalContent({
      entityId: index,
      header: "Izbriši sustav",
      text: "Jeste li sigurni da želite izbrisati sustav",
      highlightedText: systemForDeletion?.system.name,
    });
    setDeleteModalOpen(true);
  }

  function deleteSystem(index: number) {
    let copyOfSelectedSystems = [...selectedSystems.filter((x) => x.index !== index)];
    if (copyOfSelectedSystems.length === 0) {
      document.getElementById("introImage")?.scrollIntoView({ behavior: "smooth" });
    }
    setSelectedSystems(copyOfSelectedSystems);
    setDeleteModalOpen(false);
    if (copyOfSelectedSystems?.length > 0) {
      setActiveSystemIndex(0);
    }
  }

  function updateRequiredQuantity(sysIndex: number, requiredQuantity: number) {
    let updatedSystem = selectedSystems.find((x) => x.index === sysIndex);
    if (updatedSystem) {
      updatedSystem.requiredQuantity = requiredQuantity;
    }
    let copyOfSelectedSystems = [...selectedSystems.map((x) => x)];
    setSelectedSystems(copyOfSelectedSystems);
  }

  function applyApplianceThicknessChange(index: number, value: number) {
    let updatedSystem = selectedSystems.find((x) => x.index === index);
    if (updatedSystem) {
      updatedSystem.requiredApplianceThickness = value;
    }
    let copyOfSelectedSystems = [...selectedSystems.map((x) => x)];
    setSelectedSystems(copyOfSelectedSystems);
  }

  function handleOpenTotal() {
    setTotalActive(true);
  }

  function loadSystem(index: number, systemId: number) {
    setLoadingSystem(true);
    SystemsService.getById(systemId)
      .then((response) => {
        if (response) {
          updateSystemItems(index, response);
        }
      })
      .catch((e: ApiResult) => {
        modalData?.triggerError(e);
      })
      .finally(() => {
        setLoadingSystem(false);
      });
  }

  function updateSystemItems(index: number, loadedSystem: SystemResponse) {
    let projectSystem = selectedSystems.find((x) => x.index === index);
    if (projectSystem) {
      projectSystem.itemDifference = calculateNewItems(projectSystem, loadedSystem) ?? [];
      projectSystem.system = loadedSystem;
    }
    let copyOfSelectedSystems = [...selectedSystems.map((x) => x)];
    setSelectedSystems(copyOfSelectedSystems);
  }

  function calculateNewItems(oldSystem: ProjectSystem, newSystem: SystemResponse) {
    const itemDifference = newSystem.systemItems?.filter(
      ({ item: itemOne }) => !oldSystem.system?.systemItems?.some(({ item: itemTwo }) => itemOne?.id === itemTwo?.id)
    );

    return itemDifference;
  }

  useEffect(() => {
    function loadBaseSystem(systemId: number) {
      setLoadingSystem(true);
      SystemsService.getById(systemId)
        .then((response) => {
          if (response) {
            let copyOfSelectedSystems = [...selectedSystems.map((x, idx) => ({ ...x, index: idx }))];
            copyOfSelectedSystems.push(addSystem(response, copyOfSelectedSystems.length));
            setSelectedSystems(copyOfSelectedSystems);
            setActiveSystemIndex(copyOfSelectedSystems.length - 1);
            setLoadingAllSystems(true);
            setTotalActive(false);
            setTimeout(function () {
              document.getElementById("calcWrap")?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
            }, 500);
          }
        })
        .catch((e: ApiResult) => {
          if (!modalData?.content?.contentSet) {
            modalData?.triggerError(e);
          }
        })
        .finally(() => {
          setLoadingSystem(false);
        });
    }
    function addSystem(newSystem: SystemResponse, index: number | null) {
      return {
        index: index ?? selectedSystems.length,
        name: "",
        requiredQuantity: 1,
        requiredApplianceThickness: 0,
        additionalParameters: [],
        system: newSystem,
        allSystems: [],
        itemDifference: [],
      };
    }

    if (systemId) {
      loadBaseSystem(systemId);
      clearSystemAdded();
    } else if (projectId) {
      setLoadingSystem(true);
      ProjectsService.getById(Number(projectId))
        .then((response: ProjectResponse) => {
          if (response?.projectSystems) {
            removeProjectId();
            let copyOfSelectedSystems = [...selectedSystems.map((x, idx) => ({ ...x, index: idx }))];
            response.projectSystems?.forEach(function (prSys, index) {
              if (prSys.system) {
                copyOfSelectedSystems.push(addSystem(prSys.system, index));
              }
            });
            setSelectedSystems(copyOfSelectedSystems);
            setActiveSystemIndex(copyOfSelectedSystems.length - 1);

            setTotalActive(false);
            setTimeout(function () {
              document.getElementById("calcWrap")?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
              setLoadingAllSystems(true);
            }, 500);
          }
        })
        .catch((e: ApiResult) => {
          if (!modalData?.content?.contentSet) {
            modalData?.triggerError(e);
          }
        })
        .finally(() => {
          setLoadingSystem(false);
        });
    }
  }, [systemId, projectId, removeProjectId, selectedSystems, clearSystemAdded, modalData]);

  useEffect(() => {
    if (loadingAllSystems && selectedSystems?.length > 0) {
      let copyOfSelectedSystems = [...selectedSystems.map((x, idx) => ({ ...x, index: idx }))];
      if (copyOfSelectedSystems?.length > 0) {
        let activeProjectSystem = copyOfSelectedSystems[activeSystemIndex];
        if (activeProjectSystem && activeProjectSystem.system.parameterType !== SYSTEM_PARAMETER_TYPE.None && activeProjectSystem.allSystems.length === 0) {
          if (Number(activeProjectSystem?.system?.baseSystemId)) {
            SystemsService.getByBaseSystemId(Number(activeProjectSystem?.system?.baseSystemId))
              .then((response: Array<SystemResponse>) => {
                if (response) {
                  activeProjectSystem.allSystems = response;
                  setSelectedSystems(copyOfSelectedSystems);
                }
              })
              .catch((e: ApiResult) => {
                modalData?.triggerError(e);
              })
              .finally(() => {
                setLoadingAllSystems(false);
              });
          }
        }
      }
      setLoadingAllSystems(false);
    }
  }, [activeSystemIndex, loadingAllSystems, selectedSystems, modalData]);

  function handleActiveSystemSelect(index: number) {
    setActiveSystemIndex(index);
    setLoadingAllSystems(true);
    if (totalActive) {
      setTotalActive(false);
    }
  }

  return (
    <div className="u-pos-relative">
      {selectedSystems && selectedSystems.length > 0 && (
        <div id="calcWrap" className={classNames("u-flex", "u-col-sm-12", "u-col-xs-12", "u-bg-white", calcCss.calcWrap)}>
          <div className={classNames("u-col-xs-12", "u-col-sm-3", calcCss.sidebar)}>
            <Sidebar
              activeSystemIndex={activeSystemIndex}
              selectedSystems={selectedSystems}
              onSystemChange={handleActiveSystemSelect}
              onSystemRename={handleSystemRename}
              onSystemDuplicate={handleSystemDuplicate}
              onSystemDelete={handleSystemDelete}
              onSeeTotal={handleOpenTotal}
              totalActive={totalActive}
              addSystemActive={true}
            />
          </div>
          {!totalActive && activeSystemElement && (
            <div className="u-col-xs-12 u-col-sm-10">
              <System
                projectSystem={activeSystemElement}
                handleRequiredQuantity={updateRequiredQuantity}
                handleRequiredApplianceThicknessChange={applyApplianceThicknessChange}
                handleSystemChange={loadSystem}
              />
            </div>
          )}
          {totalActive && selectedSystems && selectedSystems.length > 0 && (
            <div className="u-col-xs-12 u-col-sm-10">
              <div className={classNames("u-pl5per", "u-pr5per", "u-bg-white", calcCss.totalSystems)}>
                <Header
                  showAuthModal={(formType: string) => showAuthModal(formType)}
                  showPartnerModal={(isOpen: boolean) => showPartnerModal(isOpen)}
                  selectedSystems={selectedSystems}
                />
                <Total selectedSystems={selectedSystems} />
              </div>
            </div>
          )}
        </div>
      )}
      {loadingSystem && <Loader />}
      {deleteModalOpen && deleteModalContent && (
        <DeleteModal close={(isOpen: boolean) => setDeleteModalOpen(isOpen)} deleteEntity={deleteSystem} modalContent={deleteModalContent} />
      )}
    </div>
  );
}

export default Calculator;
